import {
  BackofficeGroupPromoListItem,
  BackofficeGroupPromoListQuery,
  BackofficeGroupPromoListResponse,
} from '@diamond/shared/types';
import { apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useInfiniteQuery } from '@tanstack/react-query';
import { query as queryUrlcat } from 'urlcat';

/**
 * Fetches a paginated list of group promotions from the backoffice API.
 *
 * @param token - The authentication token for the API request.
 * @param query - The query parameters for filtering and pagination.
 * @returns A promise that resolves to an object containing the list of promotion data
 *          for the current page and the next page number, or null if it's the last page.
 * @throws Throws an error if the API request fails.
 */

export const getGroupPromoList = async (
  token: string,
  query: BackofficeGroupPromoListQuery
): Promise<{
  data: BackofficeGroupPromoListItem[];
  nextPage: number | null;
}> => {
  const apiUrl = 'backoffice/promo';
  // Construct API query parameters from the query object
  const params: Record<string, string | number | string[]> = {
    page: query.page || 1,
    size: query.size || 10,
  };

  // Append optional parameters
  if (query.customer_type) params.customer_type = query.customer_type;
  if (query.start_date) params.start_date = query.start_date;
  if (query.end_date) params.end_date = query.end_date;
  if (query.mechanism) params.mechanism = query.mechanism;
  if (query.promo_type_by_shipto) {
    params.promo_type_by_shipto = query.promo_type_by_shipto;
  }
  if (query.status) params.status = query.status;
  if (query.search_query) params.search_query = query.search_query;

  try {
    const { data: result } = await apiInstance(
      token
    ).get<BackofficeGroupPromoListResponse>(apiUrl, {
      params,
      paramsSerializer: (p) => {
        return queryUrlcat(p, { arrayFormat: 'repeat' });
      },
    });

    const currentPage = query.page || 1;
    const totalPages = result.pagination.pages;
    const nextPage = currentPage < totalPages ? currentPage + 1 : null;

    return {
      data: result.data,
      nextPage: nextPage,
    };
  } catch (error) {
    console.error('Error fetching group promo list via apiInstance:', error);
    throw error;
  }
};

export const BACKOFFICE_GROUP_PROMO_QUERY_KEY = 'BackofficeGroupPromo';

/**
 * React Query hook for fetching and managing the group promotion list data
 * with infinite scrolling
 *
 * @param query - The query parameters for filtering and pagination.
 *                Changes to this object will trigger a refetch.
 * @returns An object containing the infinite query state, data, fetch status,
 *          and functions for fetching more pages.
 */
export function useGroupPromoList(query: BackofficeGroupPromoListQuery) {
  const { access_token } = useAuthStore();

  return useInfiniteQuery({
    queryKey: [BACKOFFICE_GROUP_PROMO_QUERY_KEY, query],
    queryFn: ({ pageParam = 1 }) =>
      getGroupPromoList(access_token, { ...query, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    gcTime: 0,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });
}
